import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { SettingsItem } from "@msuite/ui/settings";
import { responseHandler } from "@msuite/ui/utils";
import { CONFIRM_DELETE_INCOMING_INVOICE } from "@msuite/utils/schemas";
import React from "react";
import { useNavigate } from "react-router";
import { Can } from "@/components/citadel";
import { client } from "@/hono";
import { useIncomingInvoice } from "@/hooks/use-incoming-invoice";
import { DeleteIncomingInvoiceModal } from "@/modals/incoming-invoices/delete-incoming-invoice-modal";
import type { IncomingInvoicePanelBaseProps } from "../../shared";

/**
 * Delete the invoice for good, behind the typed confirmation a project's deletion asks for. A
 * paid invoice cannot be deleted — its payment is taken back first, which the route insists on too.
 */
export function DeleteIncomingInvoice({ incomingInvoiceId }: IncomingInvoicePanelBaseProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const controller = useAsyncModal<string>();
	const deleteLoader = useLoading();
	const navigate = useNavigate();
	const qc = useQueryClient();

	/** Query */
	const { incomingInvoice } = useIncomingInvoice(incomingInvoiceId);

	/** Functions */
	async function handleOnDelete() {
		try {
			deleteLoader.start();
			const confirmation = await controller.confirm();
			if (confirmation !== CONFIRM_DELETE_INCOMING_INVOICE) return;
			const response = await client.incoming_invoices[":incomingInvoiceId"].$delete({
				param: { incomingInvoiceId },
				json: { confirmation },
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
			navigate("/controlling/incoming-invoices");
		} finally {
			deleteLoader.stop();
		}
	}

	/** Computed */
	const isPaid = incomingInvoice?.state === "paid";

	/** Render */
	return (
		<React.Fragment>
			<SettingsItem
				title={t("incoming_invoices.delete_invoice")}
				description={t(
					isPaid
						? "incoming_invoices.delete_paid_refused"
						: "incoming_invoices.delete_invoice_description",
				)}
			>
				<Can
					action="delete"
					subject="IncomingInvoice"
					resource={incomingInvoice ? { ...incomingInvoice } : undefined}
				>
					<Button
						isLoading={controller.isPending || deleteLoader.isLoading}
						isDisabled={!incomingInvoice || isPaid}
						variant="danger-ghost"
						onClick={handleOnDelete}
						data-cy="delete-incoming-invoice-button"
					>
						{t("incoming_invoices.delete_invoice")}
					</Button>
				</Can>
			</SettingsItem>
			<DeleteIncomingInvoiceModal controller={controller} />
		</React.Fragment>
	);
}
